import { extractPortableText } from './json-ld-utils';
import type { GenericSectionBuilder } from '~/sanity/types';

type PortableTextBlockArray = unknown[] | undefined | null;

/**
 * Scan a portable text block array for breakout sections with `summary: true`
 * and return their text content as plain strings.
 */
function extractSummariesFromPortableTextBlocks(blocks: PortableTextBlockArray): string[] {
  if (!Array.isArray(blocks)) {
    return [];
  }

  const summaryTexts: string[] = [];

  for (const block of blocks) {
    if (
      !block ||
      typeof block !== 'object' ||
      (block as { _type?: string })._type !== 'breakoutSection'
    ) {
      continue;
    }

    const breakout = block as {
      summary?: boolean;
      breakoutSectionContent?: unknown[];
    };

    if (!breakout.summary) {
      continue;
    }

    const text = extractPortableText(breakout.breakoutSectionContent);
    if (text) {
      summaryTexts.push(text);
    }
  }

  return summaryTexts;
}

/**
 * Walk a GenericSectionBuilder to find breakout sections flagged as summaries
 * and return their portable text content as a single plain-text string.
 *
 * Returns `undefined` when no summary-flagged breakout sections are found.
 */
export function extractAbstractFromSectionBuilder(
  content: GenericSectionBuilder | undefined | null
): string | undefined {
  if (!content?.sectionBuilder?.length) {
    return;
  }

  const summaryTexts: string[] = [];

  for (const section of content.sectionBuilder) {
    if (section._type !== 'sectionBuilderPortableText') {
      continue;
    }

    const portableTextBlocks = (section as { sectionBuilderPortableText?: unknown[] })
      .sectionBuilderPortableText;

    summaryTexts.push(...extractSummariesFromPortableTextBlocks(portableTextBlocks));
  }

  return summaryTexts.length ? summaryTexts.join(' ') : undefined;
}

/**
 * Walk a BlogPortableText or VideoPortableText block array to find breakout
 * sections flagged as summaries and return their content as plain text.
 *
 * Works for `blogPortableText` and `videoPortableText` arrays which embed
 * breakout sections as inline block types.
 */
export function extractAbstractFromPortableText(
  blocks: PortableTextBlockArray
): string | undefined {
  const summaryTexts = extractSummariesFromPortableTextBlocks(blocks);
  return summaryTexts.length ? summaryTexts.join(' ') : undefined;
}
